import React, { useState } from "react";
import { X, Calendar, Globe, CalendarDays } from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";
import { useTranslation } from "react-i18next";

export default function BossCreatePlace({ visible, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [placeName, setPlaceName] = useState("");
  const [profit, setProfit] = useState("");
  const [paidWay, setPaidWay] = useState(1); // 1=Monthly, 2=Daily, 3=Global
  const [placeDailySalary, setPlaceDailySalary] = useState("");
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setPlaceName("");
    setProfit("");
    setPaidWay(1);
    setPlaceDailySalary("");
    setNote("");
    onClose();
  };

  const isFormValid =
    placeName.trim() &&
    (paidWay === 1 ||
      (paidWay === 2 && placeDailySalary.trim()) ||
      (paidWay === 3 && profit.trim()));

  const handleCreatePlace = async () => {
    if (!placeName.trim()) {
      alert(t("placeNameRequired"));
      return;
    }
    if (paidWay === 2 && !placeDailySalary.trim()) {
      alert(t("dailySalaryRequired"));
      return;
    }
    if (paidWay === 3 && !profit.trim()) {
      alert(t("globalProfitRequired"));
      return;
    }

    const userData = JSON.parse(localStorage.getItem("user") || "{}");

    setIsLoading(true);
    try {
      const data1 = {
        Places: {
          Place: placeName,
          Profit:
            paidWay === 1 || paidWay === 3
              ? profit
                ? parseInt(profit)
                : 0
              : 0,
          placeType: 1,
          Note: note.trim() || null,
          PaidWay: paidWay,
          PlaceDailySalary: paidWay === 2 ? parseInt(placeDailySalary) : null,
        },
        UserId: userData.id,
      };

      const response = await axios.post(`${MyDomain}Places/CreatePlace`, data1);

      if (response.status === 200) {
        alert(t("placeCreatedSuccessfully"));
        setPlaceName("");
        setProfit("");
        setPaidWay(1);
        setPlaceDailySalary("");
        setNote("");
        if (onSuccess) onSuccess(response.data);
        onClose();
      }
    } catch (error) {
      console.error("Error creating place:", error);
      const msg =
        error.response?.data?.message ||
        error.response?.data ||
        t("failedToCreatePlace");
      alert(msg);
    } finally {
      setIsLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[90vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Handle bar ── */}
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mt-3 mb-1 sm:hidden" />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 pt-6 pb-4">
          {/* Close button */}
          <div className="flex justify-end mb-2">
            <button
              onClick={handleClose}
              className="w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Header */}
          <div className="flex flex-col items-center mb-8">
            <span className="text-5xl mb-3">📍</span>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
              {t("createNewPlace")}
            </h2>
          </div>

          {/* Form */}
          <div className="space-y-5">
            {/* Place Name */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {t("workplaceName")}
              </label>
              <div className="flex items-center gap-3 bg-gray-50 border-2 border-gray-200 rounded-2xl px-4 py-3 focus-within:border-blue-400 transition">
                <span className="text-xl flex-shrink-0">🏢</span>
                <input
                  type="text"
                  value={placeName}
                  onChange={(e) => setPlaceName(e.target.value)}
                  placeholder={t("enterWorkplaceName")}
                  maxLength={50}
                  className="flex-1 bg-transparent outline-none text-gray-900 text-base"
                />
              </div>
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {t("paymentMethod")}
              </label>

              {/* 3-button selector */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 1, label: t("monthly"), Icon: Calendar },
                  { value: 2, label: t("daily"), Icon: CalendarDays },
                  { value: 3, label: "Global", Icon: Globe },
                ].map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    onClick={() => {
                      setPaidWay(value);
                      if (value === 2) setProfit("");
                      else setPlaceDailySalary("");
                    }}
                    className={`flex items-center justify-center gap-1.5 py-3 px-2 rounded-xl border-2 text-sm font-semibold transition ${
                      paidWay === value
                        ? "bg-blue-500 border-blue-500 text-white"
                        : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Description card */}
              <div className="flex items-start gap-2.5 bg-yellow-50 border border-yellow-200 rounded-xl p-3 mt-1">
                <span className="text-base flex-shrink-0">💡</span>
                <p className="text-xs text-yellow-800 leading-relaxed">
                  {paidWay === 1 ? t("monthlyPaymentDesc") : paidWay === 2 ? t("dailyPaymentDesc") : t("globalPaymentDesc")}
                </p>
              </div>
            </div>

            {/* Daily Salary — only when paidWay === 2 */}
            {paidWay === 2 && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {t("dailySalaryPerEmployee")}
                </label>
                <div className="flex items-center gap-3 bg-gray-50 border-2 border-gray-200 rounded-2xl px-4 py-3 focus-within:border-blue-400 transition">
                  <span className="text-xl flex-shrink-0">💰</span>
                  <input
                    type="number"
                    value={placeDailySalary}
                    onChange={(e) => setPlaceDailySalary(e.target.value)}
                    placeholder={t("enterDailySalaryPerEmployee")}
                    maxLength={10}
                    className="flex-1 bg-transparent outline-none text-gray-900 text-base"
                  />
                  <span className="text-gray-500 font-semibold">₪</span>
                </div>
              </div>
            )}

            {/* Profit — when paidWay === 1 or 3 */}
            {(paidWay === 1 || paidWay === 3) && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {paidWay === 3 ? t("globalProfitLabel") : t("monthlyProfitLabel")}
                </label>
                <div className="flex items-center gap-3 bg-gray-50 border-2 border-gray-200 rounded-2xl px-4 py-3 focus-within:border-blue-400 transition">
                  <span className="text-xl flex-shrink-0">💵</span>
                  <input
                    type="number"
                    value={profit}
                    onChange={(e) => setProfit(e.target.value)}
                    placeholder={
                      paidWay === 3
                        ? t("enterGlobalProfit")
                        : t("enterProfitAmount")
                    }
                    maxLength={10}
                    className="flex-1 bg-transparent outline-none text-gray-900 text-base"
                  />
                  <span className="text-gray-500 font-semibold">₪</span>
                </div>
              </div>
            )}

            {/* Info card */}
            <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-xl p-3">
              <span className="text-base flex-shrink-0">ℹ️</span>
              <p className="text-xs text-blue-800 leading-relaxed">
                {paidWay === 1 ? t("monthlyProfitInfo") : paidWay === 2 ? t("dailySalaryRateInfo") : t("globalProfitInfo")}
              </p>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={handleCreatePlace}
            disabled={!isFormValid || isLoading}
            className={`w-full py-4 rounded-2xl text-lg font-bold tracking-wide transition ${
              !isFormValid || isLoading
                ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600 text-white shadow-lg"
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t("creating")}</span>
              </div>
            ) : (
              t("createPlace")
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}
